(function () {
  'use strict';

  if (window.YiseFrame) return;

  const reads = [];
  const writes = [];
  const keyedReads = new Map();
  let frame;

  function schedule() {
    if (frame) return;
    frame = window.requestAnimationFrame(flush);
  }

  function run(task) {
    try {
      task();
    } catch (error) {
      window.reportError?.(error);
    }
  }

  function flush() {
    frame = undefined;

    const pendingReads = reads.splice(0, reads.length);
    const pendingKeyedReads = Array.from(keyedReads.values());
    keyedReads.clear();

    pendingReads.forEach(run);
    pendingKeyedReads.forEach(run);

    writes.splice(0, writes.length).forEach(run);

    if (reads.length || writes.length || keyedReads.size) schedule();
  }

  /**
   * Queues a layout read for the shared measurement phase.
   *
   * @param {() => void} task - Callback that only reads geometry or style.
   * @returns {void}
   */
  function read(task) {
    if (typeof task !== 'function') return;
    reads.push(task);
    schedule();
  }

  /**
   * Queues a layout write for the shared mutation phase.
   *
   * @param {() => void} task - Callback that only writes to the DOM.
   * @returns {void}
   */
  function write(task) {
    if (typeof task !== 'function') return;
    writes.push(task);
    schedule();
  }

  /**
   * Queues a coalescing layout read.
   *
   * @param {unknown} key - Identity of the measurement, unique per owner and purpose.
   * @param {() => void} task - Callback that only reads geometry or style.
   * @returns {void}
   */
  function measure(key, task) {
    if (typeof task !== 'function') return;
    if (key === undefined || key === null) {
      read(task);
      return;
    }
    keyedReads.set(key, task);
    schedule();
  }

  /**
   * Drops a pending coalescing read, for owners tearing down before their
   * frame runs.
   *
   * @param {unknown} key - Key previously passed to `measure`.
   * @returns {void}
   */
  function cancelMeasure(key) {
    keyedReads.delete(key);
  }

  const lengthMemo = new Map();
  const RELATIVE_LENGTH = /^(-?(?:\d+\.?\d*|\.\d+))(px|rem|em)$/;
  let probe;

  function fontSizeOf(element) {
    return Number.parseFloat(window.getComputedStyle(element).fontSize) || 16;
  }

  function ensureProbe() {
    if (probe?.isConnected) return probe;

    probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    // Absolutely positioned, zero block-size and contained, so the probe can
    // neither shift the page nor be shifted by it.
    probe.style.cssText =
      'position:absolute;top:0;left:0;block-size:0;visibility:hidden;pointer-events:none;contain:layout style;';
    document.body.append(probe);
    return probe;
  }

  /**
   * Resolves a CSS length token to pixels.
   *
   * @param {string} token - CSS length, typically a custom property's computed value.
   * @param {Element} [contextElement] - Element whose font-size scopes `em`. Defaults to the root element.
   * @returns {number} Resolved pixel length, or `0` when the token is empty or unresolvable.
   */
  function resolveLength(token, contextElement) {
    const value = typeof token === 'string' ? token.trim() : '';
    if (!value) return 0;

    const context = contextElement instanceof Element ? contextElement : document.documentElement;
    const rootFontSize = fontSizeOf(document.documentElement);

    const relative = RELATIVE_LENGTH.exec(value);
    if (relative) {
      const amount = Number.parseFloat(relative[1]);
      if (relative[2] === 'px') return amount;
      if (relative[2] === 'rem') return amount * rootFontSize;
      return amount * fontSizeOf(context);
    }

    const contextFontSize = fontSizeOf(context);
    const memoKey = `${value}|${contextFontSize}|${rootFontSize}|${window.innerWidth}`;
    const memoized = lengthMemo.get(memoKey);
    if (memoized !== undefined) return memoized;

    const element = ensureProbe();
    element.style.fontSize = `${contextFontSize}px`;
    element.style.inlineSize = value;
    const resolved = element.getBoundingClientRect().width;
    const length = Number.isFinite(resolved) ? resolved : 0;

    lengthMemo.set(memoKey, length);
    return length;
  }

  /**
   * Resolves a custom property on an element to pixels through `resolveLength`.
   *
   * @param {Element} element - Element whose computed style carries the property.
   * @param {string} property - Custom property name, including the leading `--`.
   * @returns {number} Resolved pixel length, or `0` when unset or unresolvable.
   */
  function resolveCustomProperty(element, property) {
    if (!(element instanceof Element) || typeof property !== 'string') return 0;

    const token = window.getComputedStyle(element).getPropertyValue(property);
    return resolveLength(token, element);
  }

  const TAIL_CLEARANCE_PX = 2;

  /**
   * Reserves the trailing scroll a document needs for its last anchor target to reach the top of a scroll-spy's tracking region.
   *
   * @param {object} options - Reservation to measure.
   * @param {Element} options.target - Last tracked element, the one that must be able to reach the line.
   * @param {number} options.line - Offset in pixels from the top of the viewport where the tracking region starts.
   * @param {Element} options.host - Element the custom property is written to and read back from. Typically the owner's root, leaving the stylesheet free to apply the inherited value on whichever descendant closes the document.
   * @param {string} options.property - Custom property name, including the leading `--`.
   * @param {number} [options.clearance] - Pixels of overlap past the line.
   * @returns {number} Room now reserved, in pixels.
   */
  function reserveTailRoom(options) {
    const { target, line, host, property, clearance = TAIL_CLEARANCE_PX } = options || {};
    if (!(target instanceof Element) || !(host instanceof Element)) return 0;
    if (typeof property !== 'string' || !property.startsWith('--')) return 0;

    const doc = document.documentElement;
    const applied = resolveCustomProperty(host, property);
    const targetTop = target.getBoundingClientRect().top + window.scrollY;
    const trailing = doc.scrollHeight - targetTop - applied;
    const needed = doc.clientHeight - (Number.isFinite(line) ? line : 0) + clearance;
    const room = Math.max(0, Math.ceil(needed - trailing));
    if (room === applied) return room;

    write(() => {
      if (host.isConnected) host.style.setProperty(property, `${room}px`);
    });

    return room;
  }

  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Whether the visitor has asked for reduced motion right now.
   *
   * @returns {boolean} True when the reduce preference is set.
   */
  function prefersReducedMotion() {
    return motionQuery.matches;
  }

  window.YiseFrame = Object.freeze({
    read,
    write,
    measure,
    cancelMeasure,
    resolveLength,
    resolveCustomProperty,
    reserveTailRoom,
    motionQuery,
    prefersReducedMotion,
  });

  window.dispatchEvent(new CustomEvent('yise:frame:ready'));
})();
